(() => {
  const roots = document.querySelectorAll('[data-microwave-advisor]');
  if (!roots.length) return;

  const normalize = (value) => String(value || '').trim().toLowerCase();
  const numberFrom = (value) => {
    const match = String(value || '').replace(',', '.').match(/\d+(?:\.\d+)?/);
    return match ? Number(match[0]) : 0;
  };
  const escapeHtml = (value) => String(value || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  roots.forEach((root) => {
    const categoryButtons = [...root.querySelectorAll('[data-advisor-category]')];
    const panels = [...root.querySelectorAll('[data-advisor-panel]')];
    const form = root.querySelector('[data-microwave-form]');
    const quiz = root.querySelector('.pw-microwave-advisor__quiz');
    const results = root.querySelector('[data-advisor-results]');
    const grid = root.querySelector('[data-result-grid]');
    const summary = root.querySelector('[data-result-summary]');
    const noResults = root.querySelector('[data-no-results]');
    const nextButton = root.querySelector('[data-advisor-next]');
    const backButton = root.querySelector('[data-advisor-back]');
    const submitButton = root.querySelector('[data-advisor-submit]');
    const restartButton = root.querySelector('[data-advisor-restart]');
    const validation = root.querySelector('[data-advisor-validation]');
    const progressLabel = root.querySelector('[data-progress-label]');
    const progressBar = root.querySelector('[data-progress-bar]');
    const nicheChoices = root.querySelector('[data-niche-choices]');
    const freestandingNote = root.querySelector('[data-freestanding-note]');
    const productData = root.querySelector('[data-microwave-products]');
    const steps = [...root.querySelectorAll('[data-step]')];
    let products = [];
    let currentStep = 1;

    try {
      products = JSON.parse(productData?.textContent || '[]');
    } catch (error) {
      console.error('Magnetronadviseur: productgegevens konden niet worden gelezen.', error);
    }

    const selectCategory = (category, updateUrl = true) => {
      const allowed = ['magnetron', 'oven', 'kookplaat', 'fornuis'];
      const selected = allowed.includes(category) ? category : 'magnetron';

      categoryButtons.forEach((button) => {
        const active = button.dataset.advisorCategory === selected;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      panels.forEach((panel) => {
        panel.hidden = panel.dataset.advisorPanel !== selected;
      });

      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('type', selected);
        window.history.replaceState({}, '', url);
      }
    };

    categoryButtons.forEach((button) => {
      button.addEventListener('click', () => {
        selectCategory(button.dataset.advisorCategory);
        root.querySelector(`[data-advisor-panel="${button.dataset.advisorCategory}"]`)?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      });
    });

    const initialCategory = new URLSearchParams(window.location.search).get('type') || 'magnetron';
    selectCategory(initialCategory, false);

    if (!form || !steps.length) return;

    const placementValue = () => form.querySelector('input[name="placement"]:checked')?.value || '';

    const updateNicheState = () => {
      const isFreestanding = placementValue() === 'Vrijstaand';
      if (nicheChoices) nicheChoices.hidden = isFreestanding;
      if (freestandingNote) freestandingNote.hidden = !isFreestanding;
      form.querySelectorAll('input[name="niche"]').forEach((input) => {
        input.required = !isFreestanding;
        if (isFreestanding) input.checked = false;
      });
    };

    form.querySelectorAll('input[name="placement"]').forEach((input) => {
      input.addEventListener('change', updateNicheState);
    });

    const showStep = (stepNumber) => {
      currentStep = Math.max(1, Math.min(steps.length, stepNumber));
      steps.forEach((step) => {
        const active = Number(step.dataset.step) === currentStep;
        step.hidden = !active;
        step.classList.toggle('is-active', active);
      });

      if (progressLabel) progressLabel.textContent = `Stap ${currentStep} van ${steps.length}`;
      if (progressBar) progressBar.style.width = `${(currentStep / steps.length) * 100}%`;
      if (backButton) backButton.hidden = currentStep === 1;
      if (nextButton) nextButton.hidden = currentStep === steps.length;
      if (submitButton) submitButton.hidden = currentStep !== steps.length;
      if (validation) validation.hidden = true;

      if (currentStep === 3) updateNicheState();
      steps[currentStep - 1]?.querySelector('legend')?.focus?.();
    };

    const stepIsValid = () => {
      if (currentStep === 3 && placementValue() === 'Vrijstaand') return true;
      const current = steps[currentStep - 1];
      const radios = [...current.querySelectorAll('input[type="radio"]')];
      return !radios.length || radios.some((input) => input.checked);
    };

    nextButton?.addEventListener('click', () => {
      if (!stepIsValid()) {
        if (validation) validation.hidden = false;
        return;
      }
      showStep(currentStep + 1);
    });

    backButton?.addEventListener('click', () => showStep(currentStep - 1));

    const capacityRange = (capacity) => {
      if (capacity === 'compact') return [0, 20];
      if (capacity === 'medium') return [21, 35];
      if (capacity === 'large') return [36, Number.POSITIVE_INFINITY];
      return [0, Number.POSITIVE_INFINITY];
    };

    const scoreProduct = (product, answers) => {
      const placement = normalize(product.placement);
      const technique = normalize(product.technique);
      const selectedPlacement = normalize(answers.placement);
      const selectedTechnique = normalize(answers.technique);
      const features = normalize(product.features);
      const controls = normalize(product.controls);
      const niche = numberFrom(product.niche);
      const capacity = numberFrom(product.capacity);
      const power = numberFrom(product.power);
      const reasons = [];
      let score = 0;

      if (!placement || placement !== selectedPlacement) return null;
      score += 45;
      reasons.push(product.placement === 'Inbouw' ? 'past bij een inbouwopstelling' : 'geschikt als vrijstaand model');

      if (!technique || technique !== selectedTechnique) return null;
      score += 50;
      if (product.technique === 'Solo') reasons.push('gericht op opwarmen en ontdooien');
      if (product.technique === 'Grill') reasons.push('heeft een grill voor krokant afwerken');
      if (product.technique === 'Combi') reasons.push('combineert magnetron, grill en ovenbereiding');
      if (product.technique === 'Oven met magnetronfunctie') reasons.push('is een volwaardige oven met magnetronfunctie');

      if (selectedPlacement === 'inbouw' && answers.niche && answers.niche !== 'onbekend') {
        const selectedNiche = Number(answers.niche);
        if (niche && Math.abs(niche - selectedNiche) > 2) return null;
        if (niche) {
          score += 35;
          reasons.push(`sluit aan op een nishoogte van circa ${selectedNiche} cm`);
        } else {
          score -= 8;
          reasons.push('nismaat moet vóór bestelling worden gecontroleerd');
        }
      } else if (selectedPlacement === 'inbouw' && answers.niche === 'onbekend') {
        reasons.push('exacte nismaat moet nog worden gemeten');
      }

      const [minCapacity, maxCapacity] = capacityRange(answers.capacity);
      if (answers.capacity !== 'any') {
        if (capacity >= minCapacity && capacity <= maxCapacity) {
          score += 20;
          reasons.push(`${capacity} liter inhoud past bij de gekozen ruimtebehoefte`);
        } else if (capacity) {
          score -= 10;
        }
      }

      if (answers.priority === 'simple') {
        if (controls.includes('draai') || controls.includes('druk')) {
          score += 16;
          reasons.push('heeft een overzichtelijke fysieke bediening');
        } else if (controls) {
          score += 7;
          reasons.push(`bediening: ${product.controls}`);
        }
      }

      if (answers.priority === 'power') {
        if (power >= 900) {
          score += 18;
          reasons.push(`${power} watt magnetronvermogen voor vlot opwarmen`);
        } else if (power) {
          score += 4;
        }
      }

      if (answers.priority === 'baking') {
        if (technique === 'combi' || technique === 'oven met magnetronfunctie') {
          score += 14;
        }
        if (technique === 'grill') score += 9;
        if (features.includes('pizza')) {
          score += 8;
          reasons.push('pizzafunctie voor een krokanter resultaat');
        }
      }

      if (answers.priority === 'steam') {
        if (features.includes('stoom')) {
          score += 18;
          reasons.push('beschikt over een stoomfunctie');
        } else if (features) {
          score += 5;
          reasons.push(`extra functies: ${product.features}`);
        }
      }

      if (capacity) score += Math.min(capacity, 60) / 20;
      return { ...product, score, reasons: reasons.slice(0, 4) };
    };

    const productCard = (product, index) => {
      const meta = [
        product.placement,
        product.technique,
        product.capacity ? `${numberFrom(product.capacity)} liter` : '',
        product.power ? `${numberFrom(product.power)} watt` : ''
      ].filter(Boolean);

      const image = product.image
        ? `<div class="pw-microwave-advisor__result-image"><img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" loading="${index > 2 ? 'lazy' : 'eager'}"></div>`
        : '<div class="pw-microwave-advisor__result-image" aria-hidden="true"></div>';

      return `
        <article class="pw-microwave-advisor__result-card">
          ${image}
          <div class="pw-microwave-advisor__result-body">
            <p class="pw-microwave-advisor__result-vendor">${escapeHtml(product.vendor)}</p>
            <h3>${escapeHtml(product.title)}</h3>
            <p class="pw-microwave-advisor__result-price">${escapeHtml(product.priceFormatted)}</p>
            <div class="pw-microwave-advisor__result-meta">
              ${meta.map((item) => `<span>${escapeHtml(item)}</span>`).join('')}
            </div>
            <ul class="pw-microwave-advisor__result-reasons">
              ${product.reasons.map((reason) => `<li>${escapeHtml(reason)}</li>`).join('')}
            </ul>
            <a class="pw-button" href="${escapeHtml(product.url)}">Bekijk dit model</a>
          </div>
        </article>
      `;
    };

    const renderResults = (answers) => {
      const ranked = products
        .map((product) => scoreProduct(product, answers))
        .filter(Boolean)
        .sort((a, b) => b.score - a.score || numberFrom(a.price) - numberFrom(b.price))
        .slice(0, 6);

      if (grid) grid.innerHTML = ranked.map(productCard).join('');
      if (noResults) noResults.hidden = ranked.length > 0;
      if (summary) {
        const placeText = answers.placement === 'Inbouw'
          ? `inbouw${answers.niche && answers.niche !== 'onbekend' ? `, circa ${answers.niche} cm nis` : ''}`
          : 'vrijstaand';
        summary.textContent = ranked.length
          ? `${ranked.length} passende modellen voor ${placeText} en de uitvoering ${answers.technique.toLowerCase()}.`
          : 'Er is geen betrouwbare exacte match gevonden binnen de huidige selectie.';
      }

      if (quiz) quiz.hidden = true;
      if (results) {
        results.hidden = false;
        results.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (!stepIsValid()) {
        if (validation) validation.hidden = false;
        return;
      }

      const data = new FormData(form);
      renderResults({
        placement: data.get('placement') || '',
        technique: data.get('technique') || '',
        niche: data.get('niche') || '',
        capacity: data.get('capacity') || 'any',
        priority: data.get('priority') || ''
      });
    });

    restartButton?.addEventListener('click', () => {
      form.reset();
      updateNicheState();
      showStep(1);
      if (results) results.hidden = true;
      if (quiz) {
        quiz.hidden = false;
        quiz.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });

    showStep(1);
  });
})();
